import React from "react";
import { Box, Typography, Radio, RadioGroup } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { People, Sort, Magicpen } from "iconsax-react";
import { STRATEGIES } from "./routingApi";
import { PALETTE } from "../callsPalette";

const ICONS = { simultaneous: People, sequential: Sort, smart: Magicpen };

// Tiny schematic of each strategy — communicates the *timing* difference at a glance, which the
// title/description alone make people re-read. Decorative (aria-hidden); the text carries the meaning.
function Schematic({ type, selected }) {
  const on = selected ? PALETTE.blue.ring : PALETTE.muted.ring;
  const dot = (delay, key) => (
    <Box key={key} sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: on, opacity: delay === 0 ? 1 : 0.35 }} />
  );
  if (type === "simultaneous") {
    return (
      <Box aria-hidden sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
        {[0, 1, 2].map((i) => dot(0, i))}
        <Typography sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}>all at once</Typography>
      </Box>
    );
  }
  if (type === "sequential") {
    return (
      <Box aria-hidden sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
        {[0, 1, 2].map((i) => (
          <React.Fragment key={i}>
            {dot(i, i)}
            {i < 2 && <Box sx={{ width: 10, height: 1.5, bgcolor: "divider" }} />}
          </React.Fragment>
        ))}
        <Typography sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}>one by one</Typography>
      </Box>
    );
  }
  return (
    <Box aria-hidden sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: PALETTE.good.ring }} />
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: PALETTE.serious.ring, opacity: 0.45 }} />
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: PALETTE.muted.ring, opacity: 0.35 }} />
      <Typography sx={{ fontSize: 11, color: "text.secondary", ml: 0.5 }}>free first</Typography>
    </Box>
  );
}

/**
 * Three mutually-exclusive strategies as selectable cards. Built on a real RadioGroup (each card
 * is a <label> around a visually-hidden Radio) so keyboard arrows, screen readers and focus
 * behave natively — no hand-rolled role="radio" handling to get wrong.
 */
export default function StrategyCards({ value, onChange, disabledHints = {} }) {
  return (
    <RadioGroup
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="How incoming calls ring"
      sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}
    >
      {STRATEGIES.map((s) => {
        const Icon = ICONS[s.value];
        const selected = value === s.value;
        return (
          <Box
            key={s.value}
            component="label"
            sx={(theme) => ({
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 2,
              cursor: "pointer",
              borderRadius: 2,
              border: `1.5px solid ${selected ? PALETTE.blue.ring : theme.palette.divider}`,
              bgcolor: selected ? alpha(PALETTE.blue.ring, theme.palette.mode === "dark" ? 0.14 : 0.06) : "background.paper",
              transition: "border-color .15s, background-color .15s, box-shadow .15s",
              "&:hover": { borderColor: selected ? PALETTE.blue.ring : theme.palette.text.disabled },
              "&:has(input:focus-visible)": { boxShadow: `0 0 0 3px ${alpha(PALETTE.blue.ring, 0.35)}` },
            })}
          >
            <Radio value={s.value} sx={{ position: "absolute", opacity: 0, pointerEvents: "none" }} inputProps={{ "aria-describedby": `strategy-${s.value}-desc` }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon size={20} color={selected ? PALETTE.blue.ring : "currentColor"} variant={selected ? "Bold" : "Linear"} />
              <Typography sx={{ fontWeight: 700, fontSize: 14.5, flex: 1 }}>{s.title}</Typography>
              {s.recommended && (
                <Box
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    color: PALETTE.good.fill,
                    bgcolor: alpha(PALETTE.good.ring, 0.14),
                  }}
                >
                  RECOMMENDED
                </Box>
              )}
            </Box>
            <Typography id={`strategy-${s.value}-desc`} sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.45 }}>
              {s.description}
            </Typography>
            <Box sx={{ mt: "auto", pt: 0.5 }}>
              <Schematic type={s.value} selected={selected} />
            </Box>
            {disabledHints[s.value] && (
              <Typography sx={{ fontSize: 12, color: PALETTE.serious.fill }}>{disabledHints[s.value]}</Typography>
            )}
          </Box>
        );
      })}
    </RadioGroup>
  );
}
